import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import PortalIdParser from 'PortalIdParser';
import { createContext, useContext, useMemo } from 'react';
import { getBaseUrl, getRecordPageHref } from '../../record/internal/getRecordHref';
import { jsx as _jsx } from "react/jsx-runtime";
export const HUBSPOT_APP_LINK_TYPES = {
  ENGAGEMENT: 'ENGAGEMENT',
  CARD_SETTINGS: 'CARD_SETTINGS',
  CARD_SETTINGS_V2: 'CARD_SETTINGS_V2',
  LOCATION_SETTINGS: 'LOCATION_SETTINGS',
  CRM_OBJECT_RECORD: 'CRM_OBJECT_RECORD',
  PAGE: 'PAGE',
  CRM_OBJECT_RECORD_ALL_PROPERTIES: 'CRM_OBJECT_RECORD_ALL_PROPERTIES',
  PROSPECTING_QUEUE: 'PROSPECTING_QUEUE'
};
const CARD_TYPE_PATH_PARAM_MAP = {
  DATA_HIGHLIGHTS: 'data-highlights',
  ASSOCIATION_TABLE: 'association-tables'
};
const LOCATION_SETTINGS_PATH_PARAM_MAP = {
  CRM_RECORD_PAGE: 'record-customization'
};
const DEFAULT_LINK_HANDLERS = {
  ENGAGEMENT: {
    getHref: ({
      objectId,
      objectTypeId,
      engagementId
    }) => `${getBaseUrl()}/contacts/${PortalIdParser.get()}/record/${encodeURIComponent(normalizeTypeId(objectTypeId))}/${encodeURIComponent(objectId)}/view/1?engagement=${encodeURIComponent(engagementId)}`
  },
  CARD_SETTINGS: {
    getHref: ({
      objectTypeId,
      cardType
    }) => {
      if (!(cardType in CARD_TYPE_PATH_PARAM_MAP)) {
        throw new Error('This card type does not have a settings page');
      }
      return `${getBaseUrl()}/record-settings/${PortalIdParser.get()}/${encodeURIComponent(normalizeTypeId(objectTypeId))}/${encodeURIComponent(CARD_TYPE_PATH_PARAM_MAP[cardType])}/manage`;
    }
  },
  CARD_SETTINGS_V2: {
    getHref: ({
      objectTypeId,
      containerViewId,
      cardId,
      location
    }) => {
      if (!location) {
        return `${getBaseUrl()}/layout-editor/${PortalIdParser.get()}/${encodeURIComponent(normalizeTypeId(objectTypeId))}/edit/${encodeURIComponent(containerViewId)}/card-builder/${encodeURIComponent(cardId)}?eschref=${encodeURIComponent(window.location.pathname)}`;
      }
      return `${getBaseUrl()}/layout-editor/${PortalIdParser.get()}/${location}/${encodeURIComponent(normalizeTypeId(objectTypeId))}/edit/${encodeURIComponent(containerViewId)}/card-builder/${encodeURIComponent(cardId)}?eschref=${encodeURIComponent(window.location.pathname)}`;
    }
  },
  LOCATION_SETTINGS: {
    getHref: ({
      objectTypeId,
      location
    }) => {
      if (!(location in LOCATION_SETTINGS_PATH_PARAM_MAP)) {
        throw new Error('This location does not support settings link');
      }
      const settingsRoute = LOCATION_SETTINGS_PATH_PARAM_MAP[location];
      const eschref = `${window.location.pathname}${window.location.search}`;
      return `${getBaseUrl()}/sales-products-settings/${PortalIdParser.get()}/object/${objectTypeId}/${settingsRoute}?eschref=${encodeURIComponent(eschref)}`;
    }
  },
  CRM_OBJECT_RECORD: {
    getHref: ({
      objectTypeId,
      objectId,
      includeEschref
    }) => {
      return getRecordPageHref(objectTypeId, objectId, includeEschref !== null && includeEschref !== void 0 ? includeEschref : false);
    }
  },
  PAGE: {
    getHref: ({
      path
    }) => {
      return `${getBaseUrl()}${path}`;
    }
  },
  CRM_OBJECT_RECORD_ALL_PROPERTIES: {
    getHref: ({
      includeEschref,
      objectTypeId,
      objectId
    }) => {
      const eschref = includeEschref ? `?eschref=${encodeURIComponent(window.location.pathname)}` : '';
      return `${getBaseUrl()}/contacts/${PortalIdParser.get()}/record/${encodeURIComponent(normalizeTypeId(objectTypeId))}/${encodeURIComponent(objectId)}/properties${eschref}`;
    }
  },
  PROSPECTING_QUEUE: {
    getHref: ({
      path
    }) => {
      return `${getBaseUrl()}/prospecting/${PortalIdParser.get()}${path}`;
    }
  }
};
export function _mergeQueryWithUrl(sourceUrl, newQueryParams) {
  const url = new URL(sourceUrl);
  Object.keys(newQueryParams).forEach(key => {
    const value = newQueryParams[key];
    if (Array.isArray(value)) {
      value.forEach((val, index) => {
        // use .set for the first value to overwrite any pre-existing values in the old params
        if (index === 0) {
          url.searchParams.set(key, `${val}`);
        } else {
          url.searchParams.append(key, `${val}`);
        }
      });
    } else {
      url.searchParams.set(key, `${value}`);
    }
  });
  return url.toString();
}

/**
 * @param linkType {HubSpotAppLinkType}
 * @param linkProps {PropsForLinkType<HubSpotAppLinkType>}
 * @param extraQueryParams {Record<string, string | number | (string | number)[]>}
 *    The extraQueryParams here will overwrite any existing query params with matching names.
 *    The params also allow you to pass in an array as a param value, which is the default functionality
 *    of hub-http/helpers/params's stringify function. These params get added to the url in series, so
 *    { ids: [1, 2, 3] } becomes '?ids=1&ids=2&ids=3'
 */
export function getHrefForLinkType(linkType, linkProps, extraQueryParams = {}) {
  const defaultGetHref = DEFAULT_LINK_HANDLERS[linkType].getHref;
  const defaultHref = defaultGetHref(linkProps);
  return _mergeQueryWithUrl(defaultHref, extraQueryParams);
}
const HubSpotAppLinkContext = /*#__PURE__*/createContext(undefined);
export function useHubSpotAppLinkHandler(linkType) {
  const defaultHandler = DEFAULT_LINK_HANDLERS[linkType];
  const linkHandlerOverrides = useContext(HubSpotAppLinkContext);
  const handler = useMemo(() => {
    const linkHandlerOverride = linkHandlerOverrides && linkHandlerOverrides[linkType];
    if (!linkHandlerOverride) {
      return defaultHandler;
    }
    return Object.assign({}, defaultHandler, linkHandlerOverride);
  }, [linkType, linkHandlerOverrides, defaultHandler]);
  return handler;
}
export function HubSpotAppLinkHandlerProvider({
  value,
  children
}) {
  return /*#__PURE__*/_jsx(HubSpotAppLinkContext.Provider, {
    value: value,
    children: children
  });
}